/**
 * 간단한 확인 다이얼로그 컴포넌트 — base `Modal` 위의 확인/취소 규약.
 *
 * `@safetyhub/ui`의 `ConfirmModal`을 쓰지 않는 이유: 이 컴포넌트의 `isLoading`은 확인뿐 아니라
 * **취소·Esc·오버레이·✕를 함께 잠근다**(처리 중 닫기 금지). `ConfirmModal`의 `busy`는 확인 버튼만
 * 막아, 그대로 옮기면 처리 도중 닫히는 종전 결함이 남는다. 그래서 base `Modal`의 `closeLocked`를
 * 직접 쓴다. default 액션 규약(일반=확인 · danger=취소 자동 포커스)은 `ConfirmModal`과 같다.
 */
import { useRef } from 'react';
import { Modal, buttonClassName } from '@safetyhub/ui';

/**
 * 기본 층 — 종전 raw 오버레이의 `z-[9999]`를 **그대로** 둔다(modalLayers의 70→100 밖이다).
 *
 * ⚠️ 내리지 말 것: 이 다이얼로그는 `PhotoViewerWithEditor`(z-[9999] 전체화면 뷰어) 안의
 * `InteractiveHazardEditor`에서도 열린다. `MODAL_LAYER.*`로 내리면 그 뷰어 **뒤로 숨는다**.
 * base 레지스트리는 `z-[N]`의 N으로 최상단을 고르므로 Esc는 여전히 이 확인 하나만 닫는다.
 */
const CONFIRM_DIALOG_Z = 'z-[9999]';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmStyle?: 'danger' | 'primary';
  isLoading?: boolean;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = '확인',
  cancelText = '취소',
  confirmStyle = 'danger',
  isLoading = false,
}: ConfirmDialogProps) {
  // default 버튼(자동 포커스 대상): danger면 취소, 아니면 확인 — Enter 오조작으로 파괴적 동작이 나가지 않게.
  const defaultBtnRef = useRef<HTMLButtonElement>(null);
  const isDanger = confirmStyle === 'danger';

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      overlayZClassName={CONFIRM_DIALOG_Z}
      initialFocusRef={defaultBtnRef}
      closeLocked={isLoading}
      footer={
        <>
          <button
            type="button"
            ref={isDanger ? defaultBtnRef : undefined}
            onClick={onClose}
            disabled={isLoading}
            className={buttonClassName('secondary')}
          >
            {cancelText}
          </button>
          <button
            type="button"
            ref={isDanger ? undefined : defaultBtnRef}
            onClick={onConfirm}
            disabled={isLoading}
            className={buttonClassName(isDanger ? 'danger' : 'primary')}
          >
            {isLoading ? '처리 중...' : confirmText}
          </button>
        </>
      }
    >
      <p className="text-sm text-ink-600">{message}</p>
    </Modal>
  );
}

export default ConfirmDialog;
